"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useState } from "react";
import { useLocale } from "next-intl";
import { Configure, useSearchBox, useRefinementList, useInfiniteHits, useSortBy, useStats, useInstantSearch } from "react-instantsearch";
import { getAlgoliaIndexName } from "@/lib/algolia/config";
import { DirectoryListingView } from "../directory-listing-view.component";
import { LISTING_TYPE_CONFIGS } from "../directory-listing.config";
import type { CategoryNavItem, CategoryOption, FilterGroup, ListingType } from "../directory-listing.types";
import { hitToListingItem, type DirectoryHit } from "./hit-to-listing-item";
import { CATEGORIES_ATTRIBUTE, FIRST_LETTER_ATTRIBUTE, GROUP_KEY_TO_ATTRIBUTE, GROUP_FACET_LABEL, getEventTimeIndexName } from "./algolia-listing.config";
import { useDirectoryUrlSync } from "./use-directory-url-sync";
import type { FacetIdNameMaps, FullFacetValues } from "./directory-facet-maps";
type TimeFilter = "upcoming" | "past";

/**
 * Switches the active Algolia index to the start-date replica matching the
 * current time filter — ascending for "upcoming" (soonest event first),
 * descending for "past" (most recent first). Algolia can only sort via a
 * replica, so the order is an index choice, not a query param. Mounted only for
 * event directories; uses {@link useSortBy} so the switch flows through
 * InstantSearch's normal index refinement.
 */
function EventSortController(t0) {
  const $ = _c(18);
  const {
    timeFilter
  } = t0;
  const locale = useLocale();
  let t1;
  let t2;
  let t3;
  let t4;
  let targetIndex;
  if ($[0] !== locale || $[1] !== timeFilter) {
    const baseIndex = getAlgoliaIndexName(locale);
    targetIndex = getEventTimeIndexName(baseIndex, timeFilter);
    t4 = useSortBy;
    t3 = {
      label: "Upcoming",
      value: getEventTimeIndexName(baseIndex, "upcoming")
    };
    t1 = "Past";
    t2 = getEventTimeIndexName(baseIndex, "past");
    $[0] = locale;
    $[1] = timeFilter;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
    $[6] = targetIndex;
  } else {
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
    t4 = $[5];
    targetIndex = $[6];
  }
  let t5;
  if ($[7] !== t1 || $[8] !== t2) {
    t5 = {
      label: t1,
      value: t2
    };
    $[7] = t1;
    $[8] = t2;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t3 || $[11] !== t5) {
    t6 = {
      items: [t3, t5]
    };
    $[10] = t3;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const {
    currentRefinement,
    refine
  } = t4(t6);
  let t7;
  let t8;
  if ($[13] !== currentRefinement || $[14] !== refine || $[15] !== targetIndex) {
    t7 = () => {
      if (currentRefinement !== targetIndex) {
        refine(targetIndex);
      }
    };
    t8 = [currentRefinement, targetIndex, refine];
    $[13] = currentRefinement;
    $[14] = refine;
    $[15] = targetIndex;
    $[16] = t7;
    $[17] = t8;
  } else {
    t7 = $[16];
    t8 = $[17];
  }
  useEffect(t7, t8);
  return null;
}

/** Unix seconds at the start of a YYYY-MM-DD day (UTC). */
function dayStartUnix(date: string): number {
  return Math.floor(new Date(`${date}T00:00:00Z`).getTime() / 1000);
}

/** Unix seconds at the end of a YYYY-MM-DD day (UTC). */
function dayEndUnix(date: string): number {
  return Math.floor(new Date(`${date}T23:59:59Z`).getTime() / 1000);
}

// Show all facet values rather than Algolia's default top-10, matching the
// GROQ path which lists every referenced taxonomy option.
const FACET_LIMIT = 100;

/**
 * Minimal structural view of an InstantSearch refinement-list item — the only
 * fields this adapter reads. Avoids importing the library's deep connector type
 * (whose path is not stable across versions); the hook's richer item type is
 * structurally assignable to this.
 */
interface RefinementItem {
  value: string;
  label: string;
  isRefined: boolean;
}
interface Refinement {
  items: RefinementItem[];
  refine: (value: string) => void;
}

/** Set of currently-refined facet values. */
function refinedValues(items: RefinementItem[]): Set<string> {
  return new Set(items.filter(item => item.isRefined).map(i => i.value));
}

/**
 * Adapts InstantSearch state to the props of the shared
 * {@link DirectoryListingView}, so the Algolia-backed directory renders with
 * the exact same toolbar, filter dialog, cards, status bar and empty state as
 * the GROQ path — only the data source changes.
 *
 * A fixed set of `useRefinementList` hooks (one per facet attribute used by any
 * migrated type) is mounted so the hook order is stable; the active type's
 * `config.filterGroups` then selects which facets to surface and in what order.
 */
export function AlgoliaDirectoryView({
  listingType,
  pageSize,
  facetMaps,
  fullFacets,
  hasHeader = false,
  categoryMenu,
  categoryMenuAllHref
}: {
  listingType: ListingType;
  pageSize: number;
  facetMaps: FacetIdNameMaps;
  fullFacets: FullFacetValues;
  hasHeader?: boolean;
  categoryMenu?: CategoryNavItem[];
  categoryMenuAllHref?: string;
}) {
  const config = LISTING_TYPE_CONFIGS[listingType];
  // Articles navigate to category archives via the curated menu, so the
  // category facet is no longer URL-synced (no `?category=` for articles).
  const categoryParamEnabled = listingType !== "articles";
  const filterGroupConfigs = useMemo(() => config?.filterGroups ?? [], [config?.filterGroups]);

  // Time (upcoming/past) and date-range state — used by event listings, which
  // filter on the indexed startDate/endDate Unix timestamps. Other types leave
  // these at their defaults (their toolbar doesn't surface time/date controls).
  const [timeFilter, setTimeFilter] = useState<TimeFilter>("upcoming");
  const [dateFrom, setDateFrom] = useState<string | null>(null);
  const [dateTo, setDateTo] = useState<string | null>(null);
  // "Now" boundary for the upcoming/past filter, captured once at mount so the
  // memoised filter stays deterministic across renders.
  const [nowUnix] = useState(() => Math.floor(Date.now() / 1000));
  const filters = useMemo(() => {
    // The document type is scoped by the search client (see
    // createTypeScopedSearchClient) so it applies during the server render;
    // this string only carries the dynamic time/date filters used by events.
    const parts: string[] = [];
    if (config?.hasTimeFilter) {
      // Split on the event's LAST occurrence rather than endDate: occurrence-
      // based events (discrete occurrences[], no startDate/endDate) index
      // endDate: null and would match neither tab. lastOccurrenceUnix is the
      // same field the index prune/sort uses, so the boundary stays consistent.
      // Undated events index the far-future NO_EXPIRY_UNIX sentinel, so they
      // surface under Upcoming rather than vanishing from both tabs.
      parts.push(timeFilter === "past" ? `lastOccurrenceUnix < ${nowUnix}` : `lastOccurrenceUnix >= ${nowUnix}`);
    }
    if (config?.hasDateFilter) {
      // Overlap semantics, matching the GROQ query: an event matches the range
      // when it ends on/after `from` and starts on/before `to`.
      if (dateFrom) parts.push(`endDate >= ${dayStartUnix(dateFrom)}`);
      if (dateTo) parts.push(`startDate <= ${dayEndUnix(dateTo)}`);
    }
    return parts.join(" AND ");
  }, [config?.hasTimeFilter, config?.hasDateFilter, timeFilter, dateFrom, dateTo, nowUnix]);
  const {
    query,
    refine: setQuery
  } = useSearchBox();
  const {
    status,
    refresh
  } = useInstantSearch();
  const {
    nbHits
  } = useStats();
  const {
    items: hits,
    showMore,
    isLastPage
  } = useInfiniteHits<DirectoryHit>();
  const refinementOpts = {
    limit: FACET_LIMIT,
    sortBy: ["name:asc" as const]
  };
  // Fixed hook set — one refinement per facet attribute used by any migrated
  // listing type. Keep these explicit (not a loop) for rules-of-hooks.
  const categories = useRefinementList({
    attribute: CATEGORIES_ATTRIBUTE,
    ...refinementOpts
  });
  const district = useRefinementList({
    attribute: "district",
    ...refinementOpts
  });
  const cuisines = useRefinementList({
    attribute: "cuisines",
    ...refinementOpts
  });
  const dietaryReqs = useRefinementList({
    attribute: "dietaryReqs",
    ...refinementOpts
  });
  const foodRatings = useRefinementList({
    attribute: "foodRatings",
    ...refinementOpts
  });
  const attractionTypes = useRefinementList({
    attribute: "attractionTypes",
    ...refinementOpts
  });
  const amenities = useRefinementList({
    attribute: "amenities",
    ...refinementOpts
  });
  const starRating = useRefinementList({
    attribute: "starRating",
    ...refinementOpts
  });
  // Backs the A–Z alphabet bar. Empty until the firstLetter facet is populated
  // by a reindex, in which case DirectoryListingView simply hides the bar.
  const firstLetter = useRefinementList({
    attribute: FIRST_LETTER_ATTRIBUTE,
    limit: 30,
    sortBy: ["name:asc"]
  });

  // InstantSearchNext's server render doesn't register the refinement-list
  // widgets' (disjunctive) facets, so facet values are absent from the SSR
  // results and it skips the initial client search (hits are already
  // hydrated). Run one search on mount so the filter facets are populated
  // without waiting for the user to type. Hits are unchanged, so the grid
  // doesn't flicker.
  useEffect(() => {
    refresh();
  }, [refresh]);
  const byAttribute = useMemo<Record<string, Refinement>>(() => ({
    categories,
    district,
    cuisines,
    dietaryReqs,
    foodRatings,
    attractionTypes,
    amenities,
    starRating
  }), [categories, district, cuisines, dietaryReqs, foodRatings, attractionTypes, amenities, starRating]);
  const items = useMemo(() => hits.map(hit => hitToListingItem(hit, listingType)), [hits, listingType]);

  // Filter OPTION lists come from the full-index facet values (fetched
  // server-side), so a value only disappears when it has nothing in the whole
  // index — never just because the current search/refinement narrows it. The
  // SELECTED state below still comes from the live refinements.
  const categoryOptions = useMemo<CategoryOption[]>(() => (fullFacets[CATEGORIES_ATTRIBUTE] ?? []).map(value => ({
    _id: value,
    name: value
  })), [fullFacets]);
  const selectedCategories = useMemo(() => refinedValues(categories.items), [categories.items]);
  const filterGroups = useMemo<FilterGroup[]>(() => filterGroupConfigs.flatMap(group => {
    const attribute = GROUP_KEY_TO_ATTRIBUTE[group.key];
    if (!attribute || !byAttribute[attribute]) return [];
    const formatLabel = GROUP_FACET_LABEL[group.key];
    return [{
      key: group.key,
      label: group.label,
      options: (fullFacets[attribute] ?? []).map(value_0 => ({
        _id: value_0,
        name: formatLabel ? formatLabel(value_0) : value_0
      }))
    }];
  }), [filterGroupConfigs, byAttribute, fullFacets]);
  const selectedFilters = useMemo(() => {
    const result: Record<string, Set<string>> = {};
    for (const group_0 of filterGroupConfigs) {
      const refinement = byAttribute[GROUP_KEY_TO_ATTRIBUTE[group_0.key] ?? ""];
      if (refinement) result[group_0.key] = refinedValues(refinement.items);
    }
    return result;
  }, [filterGroupConfigs, byAttribute]);
  const availableLetters = useMemo(() => fullFacets[FIRST_LETTER_ATTRIBUTE] ?? [], [fullFacets]);
  const letterFilter = useMemo(() => firstLetter.items.find(i => i.isRefined)?.value ?? null, [firstLetter.items]);
  // URL ⇄ filter state. The URL (matching the GROQ `?category=<id>`… contract)
  // is the source of truth: these handlers write it; the hook's effect reads it
  // back and applies it to the refinements + time/date state above.
  const {
    onCategoryToggle,
    onFilterToggle,
    onApplyFilters,
    onLetterSelect,
    onSearchChange,
    onTimeFilterChange,
    onDateRangeChange,
    onClearFilters
  } = useDirectoryUrlSync({
    filterGroupConfigs,
    facetMaps,
    byAttribute,
    categories,
    categoryParamEnabled,
    firstLetter,
    query,
    setQuery,
    setTimeFilter,
    setDateFrom,
    setDateTo
  });
  const isPending = status === "loading" || status === "stalled";
  return <>
      <Configure filters={filters} hitsPerPage={pageSize} />
      {config?.hasTimeFilter && <EventSortController timeFilter={timeFilter} />}
      <DirectoryListingView items={items} total={nbHits} categoryOptions={categoryOptions} filterGroups={filterGroups} availableLetters={availableLetters} listingType={listingType} hasMore={!isLastPage} isPending={isPending} selectedCategories={selectedCategories} selectedFilters={selectedFilters} searchQuery={query} letterFilter={letterFilter} onCategoryToggle={onCategoryToggle} onClearFilters={onClearFilters} onSearchChange={onSearchChange} onLoadMore={showMore} onLetterSelect={onLetterSelect} onApplyFilters={onApplyFilters} onFilterToggle={onFilterToggle} dateFrom={dateFrom} dateTo={dateTo} onDateRangeChange={onDateRangeChange} hasDateFilter={config?.hasDateFilter ?? false} hasTimeFilter={config?.hasTimeFilter ?? false} hideAlphabetBar={config?.hideAlphabetBar ?? false} timeFilter={timeFilter} onTimeFilterChange={onTimeFilterChange} hasHeader={hasHeader} categoryMenu={categoryMenu} categoryMenuAllHref={categoryMenuAllHref} />
    </>;
}